import { Component } from '../core/smpl-components.js'
import { Eyes } from '../modules/eyes.js'

// -----------------------------------------------------------------------------
// Newsletter — the character in the title blinks like the one in the header, but
// ignores the cursor: it only looks at the e-mail field. While the field has
// focus it watches the caret, so it reads along as you type. On blur it looks
// straight ahead again.
// Selector: `.js-newsletter`, with `.js-newsletter-eyes` (the svg's parent) and
// `.js-newsletter-input` inside.
// -----------------------------------------------------------------------------

// Events after which the caret may have moved.
const CARET_EVENTS = ['focus', 'input', 'keyup', 'click', 'select']

export default class extends Component {
	mount() {
		this.elInput = this.element.querySelector('.js-newsletter-input')
		this.eyes = new Eyes(this.element.querySelector('.js-newsletter-eyes'), { pointer: false })
		this.eyes.start()

		// Text is measured on a canvas in the field's own font.
		this.ctx = document.createElement('canvas').getContext('2d')

		this.bndGetCaret = this.getCaret.bind(this)
		this.bndOnCaret = this.onCaret.bind(this)
		this.bndOnBlur = this.onBlur.bind(this)

		CARET_EVENTS.forEach((type) => this.elInput.addEventListener(type, this.bndOnCaret))
		this.elInput.addEventListener('blur', this.bndOnBlur)
	}

	unmount() {
		CARET_EVENTS.forEach((type) => this.elInput.removeEventListener(type, this.bndOnCaret))
		this.elInput.removeEventListener('blur', this.bndOnBlur)
		cancelAnimationFrame(this.raf)

		this.eyes.destroy()
		this.eyes = null
	}

	// Wait a frame: the field scrolls its text after the event, not before.
	onCaret() {
		cancelAnimationFrame(this.raf)
		this.raf = requestAnimationFrame(() => this.eyes.lookAt(this.bndGetCaret))
	}

	onBlur() {
		cancelAnimationFrame(this.raf)
		this.eyes.lookAt(null)
	}

	// The caret's viewport position: the width of the text before it, from the
	// start of the content box, minus however far the field has scrolled. With an
	// empty field that is the start of the placeholder.
	getCaret() {
		const input = this.elInput
		const styles = getComputedStyle(input)
		const rect = input.getBoundingClientRect()

		const left = rect.left + parseFloat(styles.borderLeftWidth) + parseFloat(styles.paddingLeft)
		const right = rect.right - parseFloat(styles.borderRightWidth) - parseFloat(styles.paddingRight)
		const before = input.value.slice(0, input.selectionEnd ?? input.value.length)

		this.ctx.font = styles.font
		const spacing = parseFloat(styles.letterSpacing) || 0
		const width = this.ctx.measureText(before).width + spacing * before.length

		return {
			x: Math.min(left + width - input.scrollLeft, right),
			y: rect.top + rect.height / 2,
		}
	}
}
